/* eslint-disable max-lines */
import _ from 'underscore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import Form from 'pages/shared/Form'
import PreviewField from 'pages/shared/TrendForm/PreviewField'
import TrendAlerts from 'pages/shared/TrendForm/TrendAlerts'
import ChartOptions from 'pages/shared/TrendForm/ChartOptions'
import FormSection from 'pages/shared/FormSection'

import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
import ListSelector from 'components/ListSelectAndSort'
import LabelledField from 'components/LabelledField'
import ErrorMessage from 'components/ErrorMessage'

import useDeviceMetrics from 'hooks/useDeviceMetrics'
import { useStoreContext } from 'hooks/StoreContext'
import useGroups from 'hooks/useGroups'

import { scrubText } from 'helpers/inputCleaners'

import TrendModel, { defaultTrend } from 'models/Trend'

import { color } from 'styles/colors'
import { LoadingSinglePanePage, screenSize } from 'styles/shared'

import { Trend, TrendErrors } from 'types/DeviceMetric'

import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

interface List {
  id: string | number,
  tag: string | null,
  title: string,
  color?: string | undefined,
  type: 'metric' | 'group',
  unit: string
}

type Props = {
  saveMethod: 'create' | 'update',
  trend?: Trend
}

const trendModel = new TrendModel()

const TrendForm = ({ saveMethod, trend = defaultTrend }: Props) => {
  const navigate = useNavigate()
  useDeviceMetrics()

  const { deviceMetrics, areDeviceMetricsLoaded, setAreTrendsLoaded } = useStoreContext()
  const { groups, areGroupsLoaded } = useGroups()

  const [formAttributes, setFormAttributes] = useState<Trend>(trend)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedItems, setSelectedItems] = useState<List[]>([])
  const [showPreview, setShowPreview] = useState(false)

  const alertList = formAttributes.alertList

  const { draft, name, metricList, groupList } = formAttributes
  const [errors, setErrors] = useState<Partial<TrendErrors>>({ name: '', selectedList: '' })
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()
  const componentType = 'Trend'

  const onchange = (updateAttributes) => {
    setFormAttributes({ ...formAttributes, ...updateAttributes })
  }

  const onAlertListChange = (alertList) => {
    const sortedAlerts = alertList.map((item, index) => ({ id: item.id, title: item.title, code: item.id, sortPosition: index }))
    setFormAttributes((prevAttributes) => ({ ...prevAttributes, alertList: sortedAlerts }))
  }

  const onListChange = (selectedItems) => {
    const metricIds = new Set(deviceMetrics.map(({ id }) => id))
    const metricList: { metricId: string; sortPosition: number; unit: string, draft: boolean, showInTooltip: boolean }[] = []
    const groupList: { groupId: number; sortPosition: number; unit: string, draft: boolean, showInTooltip: boolean }[] = []
    selectedItems.forEach(({ id, unit, tag, showInTooltip = false }, index) => {
      const item = { sortPosition: index + 1 }
      if (metricIds.has(id)) {
        metricList.push({ ...item, metricId: id, unit, showInTooltip, draft: tag === 'draft' })
      } else {
        groupList.push({ ...item, groupId: id, unit, showInTooltip, draft: tag === 'draft' })
      }
    })

    setFormAttributes((prevAttributes) => ({ ...prevAttributes, metricList, groupList }))

    setSelectedItems(selectedItems)
    setShowPreview(false)
  }

  useEffect(() => {
    if (areDeviceMetricsLoaded && areGroupsLoaded) {
      const combinedList = (metricList, groupList) => {
        return [
          ...(metricList || []).map(list => {
            const metric = _.find(deviceMetrics, (metric) => metric.id === list.metricId)
            return {
              id: list.metricId,
              title: metric.name,
              sortPosition: list.sortPosition,
              tag: metric.draft ? 'draft' : null,
              showInTooltip: list.showInTooltip,
              color: metric.color,
              unit: metric.unit,
              type: 'metric'
            }
          }
          ),
          ...(groupList || []).map(list => {
            const group = _.find(groups, (group) => group.groupId === list.groupId)

            return {
              id: list.groupId,
              sortPosition: list.sortPosition,
              title: group?.name,
              tag: group?.draft ? 'draft' : null,
              showInTooltip: list.showInTooltip,
              unit: group?.unit,
              type: 'group'
            }
          })
        ]
      }

      const sortedSelectedList = _.chain(combinedList(metricList, groupList))
        .sortBy((list) => list.sortPosition)
        .map((list) => (
          _.omit(list, 'sortPosition')
        )).value()

      setSelectedItems(_.values(sortedSelectedList))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areDeviceMetricsLoaded, areGroupsLoaded, trend, groups])

  const onSave = useCallback(() => {
    setIsSaving(true)

    const onSaveSuccessCallback = () => {
      setAreTrendsLoaded(false)
      navigate('/admin/trends')
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallback = () => {
      setIsSaving(false)
      alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.SAVE_FAILED, description: '' })
    }

    trendModel.save(formAttributes, saveMethod, onSaveSuccessCallback, onSaveFailureCallback, onValidationErrorCallback)
  }, [navigate, formAttributes, saveMethod, setAreTrendsLoaded, alert])

  const onCancel = async () => {
    const hasChanges = !_.isEqual(formAttributes, { ...trend })

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })

    if (!hasChanges || (hasChanges && isConfirmed)) {
      navigate('/admin/trends')
    }
  }

  const onDelete = useCallback(async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description:
        CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      setIsDeleting(true)

      const onDeleteSuccessCallback = () => {
        setAreTrendsLoaded(false)
        navigate('/admin/trends')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)
        alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.DELETE_FAILED, description: '' })
      }

      trendModel.delete(
        onDeleteSuccessCallback,
        onDeleteFailureCallback,
        trend.id
      )
    }
  }, [navigate, trend.id, setAreTrendsLoaded, confirm, alert])

  const getAvailableMetrics = () => {
    const selectedIds = selectedItems.length > 0 ? _.pluck(selectedItems, 'id') : undefined

    const allDeviceMetrics = deviceMetrics.map(({ id, name, draft, color, unit, showInTooltip }) => {
      return { id, title: name, tag: draft ? 'draft' : null, color, unit, showInTooltip }
    })

    return selectedIds ? _.filter(allDeviceMetrics, metric => !_.includes(selectedIds, metric.id)) : allDeviceMetrics
  }

  const getSelectedAlerts = () => {
    const aList: { id: string | undefined; code: string | undefined; title: string | undefined}[] = []
    if (alertList) {
      alertList.forEach(({ code }) => {
        aList.push({
          id: code,
          title: code,
          code
        })
      })
    }

    return aList
  }

  const getAvailableGroups = () => {
    const selectedIds = selectedItems.length > 0 ? _.pluck(selectedItems, 'id') : undefined
    const allGroups = groups.map(({ groupId, name, draft, unit }) => ({ id: groupId, title: name, tag: draft ? 'draft' : null, type: 'group', unit }))

    return selectedIds ? _.filter(allGroups, group => !_.includes(selectedIds, group.id)) : allGroups
  }

  const onPreviewButton = () => {
    const validationErrors = trendModel.validate(formAttributes)
    if (_.isEmpty(validationErrors) && !showPreview) {
      setShowPreview(true)
    } else {
      setShowPreview(false)
    }
    setErrors(validationErrors)
  }

  if (!areDeviceMetricsLoaded || !areGroupsLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <Form
      fullScreen
      isDeleting={isDeleting}
      isSaving={isSaving}
      deleteButtonText={'Trend'}
      displayDeleteButton={saveMethod === 'update'}
      onSaveCallback={onSave}
      onCancelCallback={onCancel}
      onDeleteCallback={onDelete}
    >
      <S.Subtitle>Trend</S.Subtitle>
      <Checkbox
        label="Draft"
        checked={draft}
        onChange={() => onchange({ draft: !draft })}
      />
      <S.TextInput
        label="Name"
        isRequired
        onChange={event => onchange({ name: scrubText(event.target.value, true) })}
        value={name || ''}
        errorMessage={errors?.name}
      />
      <ChartOptions
        hideChart={formAttributes.hideChart}
        hideRanges={formAttributes.hideRanges}
        showZeroWaterMark={formAttributes.showZeroWaterMark}
        showAlert={formAttributes.showAlert}
        drawLines={formAttributes.drawLines}
        plotZero={formAttributes.plotZero}
        waterMark={formAttributes.waterMark}
        onChangeCallback={onchange}
      />
      <TrendAlerts
        TrendAlerts={getSelectedAlerts()}
        onAlertListChangeCallback={onAlertListChange}
      />
      <FormSection title="Select Metrics/Groups">
        <ListSelector
          subTitle="Add Metrics/Groups"
          selectedListLabel="Selected Metrics/Groups"
          availableListLabel="Available Metrics/Groups"
          initialSelected={selectedItems}
          initialAvailable={[...getAvailableMetrics(), ...getAvailableGroups()]}
          onChangeCallback={onListChange}
          showTooltipCheckbox
        />
        <S.ErrorMessage errorMessage={errors.selectedList} />
      </FormSection>
      {selectedItems.length > 0 &&
        <Button
          decoration="positive"
          onPress={onPreviewButton}
          text={!showPreview ? 'Preview Trend' : 'Hide Preview'}
          styles={{ button: { marginTop: 15, marginBottom: 15 } }}
          aria-label={!showPreview ? 'preview-trend' : 'hide-preview'}
        />
      }
      {showPreview &&
        <S.PreviewField
          trend={formAttributes}
          metricList={deviceMetrics}
          groupList={groups}
          list={selectedItems}
          alerts={[]}
        />
      }
    </Form>
  )
}

export default TrendForm

const S = {
  Form: styled(Form)`
    max-width: 100%;
  `,
  Subtitle: styled.h2`
    margin-top: 20px;
    border-bottom: solid 1px;
    border-color: ${color('border')};
    padding-bottom: 10px;
    text-transform: uppercase;
  `,
  SelectedListField: styled(LabelledField)`
    padding-top: 20px;
  `,
  TextInput: styled(TextInput)`
    width: 50%;

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  PreviewField: styled(PreviewField)`
    margin-top: 50px;
    padding: 20px;
  `,
  ErrorMessage: styled(ErrorMessage)`
    padding-top: 5px;
    margin-bottom: -25px;
  `
}
